import { z } from "zod/v4";
import { createEndpointBase } from "./core";
import {
  IssueTrackingConfigurationSchemas,
  IssueTrackingRuleSchemas,
  IssueTrackingToolSchema,
  IssueTrackingToolSchemas,
} from "../ea.schema";
import { CountResponseSchema } from "../ea.schema.endpoints";
import {
  GetRequestBaseResponseSchema,
  IssueTrackingConfigurationsFilterSchema,
  IssueTrackingRulesFilterSchema,
  IssueTrackingToolsFilterSchema,
} from "../ea.schema.query";
import {
  AzureDevopsConnectionDataSchema,
  DevopsProjectsExportParamsSchema,
  DevopsScriptExportParamsSchema,
  TestCasesQuerySchema,
  TestPlansQuerySchema,
  TestSuiteSiblingsQuerySchema,
} from "../ea.schema";
import { WorkItemDefinitionSchema, WorkItemSchemas } from "../ea.schema";

const base = "issue-tracking-tools";
const itt = createEndpointBase(base);
const configurations = createEndpointBase(base, "configurations");
const rules = createEndpointBase(base, "rules");
const azureDevops = createEndpointBase(base, "azure-devops");
const integration = createEndpointBase(base, "integration");

export const issueTrackingToolsEndpoints = {
  issueTrackingToolsGet: itt({
    method: "GET",
    path: "",
    request: z.object({ filter: IssueTrackingToolsFilterSchema.optional() }).optional(),
    response: GetRequestBaseResponseSchema(IssueTrackingToolSchema),
    description: "Retrieves a list of issue tracking tools with filtering support.",
  }),
  issueTrackingToolsCount: itt({
    method: "GET",
    path: "count",
    request: z.any(),
    response: CountResponseSchema,
    description: "Returns the total number of issue tracking tools matching the filters.",
  }),
  issueTrackingToolsCreate: itt({
    method: "POST",
    path: "",
    request: IssueTrackingToolSchemas["createData"],
    response: IssueTrackingToolSchemas["general"],
    description: "Creates a new issue tracking tool configuration.",
  }),
  issueTrackingToolsUpdate: itt({
    method: "PATCH",
    path: ":id",
    request: IssueTrackingToolSchemas["update"],
    response: IssueTrackingToolSchemas["general"],
    description: "Updates an existing issue tracking tool configuration.",
  }),
  issueTrackingToolsDelete: itt({
    method: "DELETE",
    path: ":id",
    request: z.looseObject({}).optional(),
    response: IssueTrackingToolSchemas["general"],
    description: "Permanently deletes an issue tracking tool configuration.",
  }),

  issueTrackingToolsConfigurationsGet: configurations({
    method: "GET",
    path: "",
    request: z.object({ filter: IssueTrackingConfigurationsFilterSchema.optional() }).optional(),
    response: GetRequestBaseResponseSchema(IssueTrackingConfigurationSchemas["general"]),
    description: "Retrieves a list of issue tracking tool configurations with filtering support.",
  }),
  issueTrackingToolsConfigurationsCount: configurations({
    method: "GET",
    path: "count",
    request: z.any(),
    response: CountResponseSchema,
    description:
      "Returns the total number of issue tracking tool configurations matching the filters.",
  }),
  issueTrackingToolsConfigurationsCreate: configurations({
    method: "POST",
    path: "",
    request: IssueTrackingConfigurationSchemas["createData"],
    response: IssueTrackingConfigurationSchemas["general"],
    description: "Creates a new issue tracking tool configuration.",
  }),
  issueTrackingToolsConfigurationsUpdate: configurations({
    method: "PATCH",
    path: ":id",
    request: IssueTrackingConfigurationSchemas["update"],
    response: IssueTrackingConfigurationSchemas["general"],
    description: "Updates an existing issue tracking tool configuration.",
  }),
  issueTrackingToolsConfigurationsDelete: configurations({
    method: "DELETE",
    path: ":id",
    request: z.any(),
    response: z.any(),
    description: "Permanently deletes an issue tracking tool configuration.",
  }),
  issueTrackingToolsConfigurationsFetch: configurations({
    method: "GET",
    path: "fetch",
    request: z.any(),
    response: z.any(),
    description: "Fetches issue tracking tool configurations from an external service.",
  }),

  issueTrackingToolsRulesGet: rules({
    method: "GET",
    path: "",
    request: z.object({ filter: IssueTrackingRulesFilterSchema.optional() }).optional(),
    response: GetRequestBaseResponseSchema(IssueTrackingRuleSchemas["general"]),
    description: "Retrieves a list of issue tracking rules with filtering support.",
  }),
  issueTrackingToolsRulesCount: rules({
    method: "GET",
    path: "count",
    request: z
      .object({ filter: IssueTrackingRulesFilterSchema.pick({ where: true }).optional() })
      .optional(),
    response: CountResponseSchema,
    description: "Returns the total number of issue tracking rules matching the filters.",
  }),
  issueTrackingToolsRulesCreate: rules({
    method: "POST",
    path: "",
    request: IssueTrackingRuleSchemas["createData"],
    response: IssueTrackingRuleSchemas["general"],
    description: "Creates a new issue tracking rule.",
  }),
  issueTrackingToolsRulesUpdate: rules({
    method: "PATCH",
    path: ":id",
    request: IssueTrackingRuleSchemas["update"],
    response: IssueTrackingRuleSchemas["general"],
    description: "Updates an existing issue tracking rule.",
  }),
  issueTrackingToolsRulesDelete: rules({
    method: "DELETE",
    path: ":id",
    request: z.any(),
    response: z.any(),
    description: "Permanently deletes an issue tracking rule.",
  }),

  issueTrackingToolsAzureDevopsCreateWorkitem: azureDevops({
    method: "POST",
    path: "create-workitem",
    request: WorkItemDefinitionSchema,
    response: WorkItemSchemas["general"],
    description: "Sends or processes data within the integration tasks (Work Items) resource.",
  }),
  issueTrackingToolsAzureDevopsIterations: azureDevops({
    method: "GET",
    path: "iterations",
    request: AzureDevopsConnectionDataSchema,
    response: z.object({ path: z.string() }).array(),
    description: "Retrieves iterations for Azure DevOps.",
  }),
  issueTrackingToolsAzureDevopsProjects: azureDevops({
    method: "GET",
    path: "projects",
    request: AzureDevopsConnectionDataSchema,
    response: z.object({ name: z.string() }).array(),
    description: "Retrieves projects for Azure DevOps.",
  }),
  issueTrackingToolsAzureDevopsWorkitemTypes: azureDevops({
    method: "GET",
    path: "workitem-types",
    request: AzureDevopsConnectionDataSchema,
    response: z.object({ name: z.string() }).array(),
    description: "Retrieves work item types for Azure DevOps.",
  }),
  issueTrackingToolsAzureDevopsFields: azureDevops({
    method: "GET",
    path: "fields",
    request: AzureDevopsConnectionDataSchema,
    response: z.any(),
    description: "Retrieves fields for Azure DevOps.",
  }),
  issueTrackingToolsAzureDevopsAreas: azureDevops({
    method: "GET",
    path: "areas",
    request: AzureDevopsConnectionDataSchema,
    response: z.object({ path: z.string() }).array(),
    description: "Retrieves areas for Azure DevOps.",
  }),

  issueTrackingToolsIntegrationExport: integration({
    method: "POST",
    path: "export",
    request: DevopsProjectsExportParamsSchema,
    response: z.any(),
    description: "Exports the project tree to an integration tool.",
  }),
  issueTrackingToolsIntegrationTestPlans: integration({
    method: "GET",
    path: "test-plans",
    request: TestPlansQuerySchema,
    response: z.any(),
    description: "Retrieves test plans from an integration tool.",
  }),
  issueTrackingToolsIntegrationTestSuiteSiblings: integration({
    method: "GET",
    path: "test-suite-siblings",
    request: TestSuiteSiblingsQuerySchema,
    response: z.any(),
    description: "Retrieves test suite siblings from an integration tool.",
  }),
  issueTrackingToolsIntegrationTestCases: integration({
    method: "GET",
    path: "test-cases",
    request: TestCasesQuerySchema,
    response: z.any(),
    description: "Retrieves test cases from an integration tool.",
  }),
  issueTrackingToolsIntegrationExportScript: integration({
    method: "POST",
    path: "export-script",
    request: DevopsScriptExportParamsSchema,
    response: z.any(),
    description: "Exports a script to an integration tool.",
  }),
} as const;
